/** @format */

import React, { useEffect, useState } from "react"
import { StarFilled, ClockFilled } from "@ui-library/icons"
import { ChipIconElements, Container, ContractSearchIcon, StyledAutoComplete, StyledChip } from "./ContractsAutocomplete.styled";
import { useRelativeRouter } from '../../../shared/hooks/useRelativeRouter';
import { ContractDropListType, ContractsAutocompleteProps } from "./ContractsAutocomplete.types";
import { Contract, getProductTypeEnumFromId } from "_components/organisms/Dashboard/Dashboard.types";
import { ChangeUrlQueryValues } from "_shared/helpers/routerHelper";
import { ProductTypeKeysMapper } from "_components/molecules/ConsumptionSummary/ConsumptionSummary.types";

export const ContractsAutocomplete = (props: ContractsAutocompleteProps) => {
	const MIN_SIZE_TO_DISPLAY_RECENT_SEARCHES = 6;
	const MAX_SIZE_RECENT_SEARCHES = 3;
	const { contracts, selectedContract, isLoading, onSelect } = props;
    const router = useRelativeRouter();
	
	const generateLabel = (contract) => {
		const produit = ProductTypeKeysMapper[getProductTypeEnumFromId(contract.productId?.toString())];
		const disponibleText = contract.available > 1 ? "disponibles" : "disponible";
		return `<div>
			<p id="contract-id">Contrat ${contract.contractId.toString()}</p>
			<p class="market-details"><b> ${contract.available} ${produit} ${disponibleText}</b> sur ${contract.total}</p>
			<p class="market-details">${contract.marketName}</p>
		</div>`
	}


	const generateContractsArray = (contracts : Contract[]) => {
		let array : ContractDropListType[] = [];
		if(typeof window !== "undefined" && contracts?.length > MIN_SIZE_TO_DISPLAY_RECENT_SEARCHES){
			const item = localStorage.getItem('rechercheContrats');
			const localStorageContracts = item ? JSON.parse(item) : [];
			const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));
			const validStorageContracts = localStorageContracts.filter(contract => new Date(contract.date) > sevenDaysAgo);
			localStorage.setItem('rechercheContrats', JSON.stringify(validStorageContracts));
			const validValuesForCurrentNetwork = validStorageContracts.filter(v => contracts.find(c => c.contractId === v.contractId));
			if(validValuesForCurrentNetwork.length > 0){
				array = [{
					title: "Recherches récentes",
					key: "recentSearches",
					list: validValuesForCurrentNetwork.sort((n1,n2) => new Date(n2.date).getTime() - new Date(n1.date).getTime())
							.splice(0, MAX_SIZE_RECENT_SEARCHES).map((contract, index) => ({
								label: generateLabel(contract),
								key: `rc-subitem-${index + 1}`,
								value: contract.contractId.toString(),
								icon: ClockFilled,
							}))
				}]
			}
		}
		
		array.push({
			title: contracts.length > 0 ? `Tous les contrats (${contracts.length})` : "Aucun contrat trouvé ...",
			key: "allcontracts",
			list: contracts.length > 0 ? contracts
				.map((contract, index) => ({
					label: generateLabel(contract),
					key: `ac-subitem${index + 1}`,
					value: contract.contractId.toString(),
					icon: StarFilled,
			})) : [],
		});
	
		return array;
	}

    const [contractsArray, setContractsArray] = useState<ContractDropListType[]>(generateContractsArray(contracts));

	const handleSelect = value => {
		setContractsArray(generateContractsArray(contracts));
		const contrat = contracts.find(contract => contract.contractId.toString() === value);
        const newQuery = { 
			...router.query,
        	productId :  contrat?.productId.toString(),
        	marketId: contrat?.marketId.toString(),
        	contractId: value,
			searchTerm: undefined
		};
		onSelect(value);
		addOrReplaceToLocalStorage(contrat);
		router.push(ChangeUrlQueryValues(router.asPath, newQuery));
	}

	const redirectToDashboardWithoutContract = () => {
		let newQuery = { ...router.query }
		delete newQuery.productId;
		delete newQuery.marketId;
		delete newQuery.contractId;
		
		
		router.replace({
			pathname: '/home',
			query: newQuery
		}, undefined, { shallow: true });
	}

	const inputRef = React.useRef<HTMLInputElement>(null);
	
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.disabled = !!selectedContract;
		}
	}, [selectedContract]);

	const addOrReplaceToLocalStorage = (selectedContract) => {
		const selectedContractWithDate =  {...selectedContract, date: new Date()};
		const item = localStorage.getItem('rechercheContrats');
		const parsedItem = item ? JSON.parse(item) : [];
	
		const updatedContracts = parsedItem.filter(i => i.contractId !== selectedContract?.contractId);
		updatedContracts.push(selectedContractWithDate);
	
		localStorage.setItem('rechercheContrats', JSON.stringify(updatedContracts));
	}

	return (
		<Container isLoading={isLoading}>
			<ChipIconElements>
				<ContractSearchIcon/>
				{ (!!selectedContract) &&
				<StyledChip
					label={`Contrat ${selectedContract}`}
					kind="textfield"
					onClose={()=> {
						setContractsArray(generateContractsArray(contracts));
						onSelect(undefined);
					}}
				/>}
			</ChipIconElements>
			
			<StyledAutoComplete
				key={selectedContract}
				ref={inputRef}
				list={contractsArray}
				onUpdate={(val, updateList)=> {
					const filteredContracts = contracts.filter(contract => `Contrat ${contract.contractId.toString()}`.includes(val));
					updateList(generateContractsArray(filteredContracts));
				}}
				onSelect={handleSelect}
				placeholder={!selectedContract ? 'Rechercher un contrat' : undefined}
				onClear={redirectToDashboardWithoutContract}
				clearable					
			/>
		</Container>
	)
}

